import React from 'react';
import { User } from '../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import { useUser } from '../../../../hooks/useUser';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { IApiConfig, useApi } from '../../../../hooks/useApi';

export const useAssetCheck = (props: {
    onAssetsPresent: () => void;
    onAssetsAbsent: () => void;
    onAssetCheckError: () => void;
}) => {
    const { onAssetsAbsent, onAssetsPresent, onAssetCheckError } = props;
    const [checkInitiated, setCheckInitiated] = React.useState(false);

    const { api } = useAppConfigContext();
    const invokingUser = useUser();

    const [userToCheck, setUserToCheck] = React.useState<User | undefined>(
        undefined,
    );

    const assetCheckApiConfig = React.useMemo<IApiConfig>(
        () => ({
            url: `${api!.quicksightDashboards}/v1/data/dashboards/tenants/${invokingUser?.tenantIdentifier}/users/${encodeURIComponent(userToCheck?.userId || '')}/assets/has`,
            useAuth: true,
            executeType: 'manual',
            method: 'GET',
            useCache: false,
        }),
        [api, invokingUser, userToCheck],
    );

    const { data, error, execute, loading, reset } = useApi<{
        hasAssets: boolean;
    }>(assetCheckApiConfig);

    React.useEffect(() => {
        if (checkInitiated) {
            execute();
        }
    }, [execute, checkInitiated]);

    React.useEffect(() => {
        if (data && !loading && !error) {
            if (data.hasAssets) {
                onAssetsPresent();
            } else {
                onAssetsAbsent();
            }
            reset();
            setCheckInitiated(false);
            setUserToCheck(undefined);
        }
    }, [data, error, loading, onAssetsAbsent, onAssetsPresent, reset]);

    React.useEffect(() => {
        if (error && !loading) {
            reset();
            setCheckInitiated(false);
            setUserToCheck(undefined);
            onAssetCheckError();
        }
    }, [error, loading, onAssetCheckError, reset]);

    const checkUserAssets = React.useMemo(
        () => (user: User) => {
            setUserToCheck(user);
            setCheckInitiated(true);
        },
        [],
    );

    return { checkUserAssets, assetCheckInProgress: checkInitiated };
};
